import Decimal from "break_eternity.js";

export type HugeNumber = Decimal;

export type HugeNumberInput = HugeNumber | number | string;

const PLAIN_OR_SCIENTIFIC_DECIMAL_PATTERN =
  /^[+-]?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?$/;

export function huge(value: HugeNumberInput): HugeNumber {
  if (isHugeNumber(value)) {
    return value;
  }

  return new Decimal(value);
}

export function hugeZero(): HugeNumber {
  return new Decimal(0);
}

export function hugeOne(): HugeNumber {
  return new Decimal(1);
}

export function parseHugeInput(input: string): HugeNumber | null {
  const trimmedInput = input.trim();

  if (trimmedInput === "") {
    return null;
  }

  if (!PLAIN_OR_SCIENTIFIC_DECIMAL_PATTERN.test(trimmedInput)) {
    return null;
  }

  const parsed = new Decimal(trimmedInput);

  if (!parsed.isFinite()) {
    return null;
  }

  return parsed;
}

export function serializeHuge(value: HugeNumber): string {
  return value.toString();
}

export function deserializeHuge(value: unknown): HugeNumber {
  if (isHugeNumber(value)) {
    return value.isFinite() ? value : hugeZero();
  }

  if (typeof value === "string") {
    return parseHugeInput(value) ?? hugeZero();
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return new Decimal(value);
  }

  return hugeZero();
}

export function isHugeNumber(value: unknown): value is HugeNumber {
  return value instanceof Decimal;
}

export function isNonNegativeHuge(value: HugeNumber): boolean {
  return value.gte(hugeZero());
}

export function compareHuge(a: HugeNumber, b: HugeNumberInput): number {
  return a.cmp(huge(b));
}

export function multiplyHuge(
  a: HugeNumberInput,
  b: HugeNumberInput,
): HugeNumber {
  return huge(a).mul(huge(b));
}

export function maxHuge(a: HugeNumber, b: HugeNumberInput): HugeNumber {
  const normalizedB = huge(b);

  return compareHuge(a, normalizedB) >= 0 ? a : normalizedB;
}

export function minHuge(a: HugeNumber, b: HugeNumberInput): HugeNumber {
  const normalizedB = huge(b);

  return compareHuge(a, normalizedB) <= 0 ? a : normalizedB;
}

export function ceilHugeAtLeast(
  value: HugeNumberInput,
  minimum: HugeNumberInput,
): HugeNumber {
  return maxHuge(huge(value).ceil(), minimum);
}

// 10^log10Value as a HugeNumber. For exponents in break_eternity's layer-1
// magnitude range the given components are kept bit-exact (no pow round-trip);
// outside that range normalization applies the usual conversion.
export function hugeExp10(log10Value: number): HugeNumber {
  return Decimal.fromComponents(1, 1, log10Value);
}
